// Shared data-download module for NoticeAI list pages.
//
// Generates properly formatted Excel (.xlsx via SheetJS) and PDF (jsPDF)
// files from a list of records. Independent of the table's VISUAL columns
// — each entity declares its own clean export column set here, so the
// output never carries empty action columns (Reassign / Sync buttons) and
// never leaks sensitive fields (client passwords are deliberately omitted).
//
// Public API:
//   downloadExcel(records, columnSet, { fileName, title })
//   downloadPdf(records, columnSet, { fileName, title })
//   getColumnSet(entity)   entity: "clients-income-tax" | "clients-gst" |
//                          "clients-tds" | "notices-gst" | "notices-it-ep"
//                          | "notices-it-demand" | "notices-tds"
//
// A "column set" is an array of { header, accessor } where `accessor` is
// either a string key into the record or a fn(record) -> string.

import { saveAs } from "file-saver";
import moment from "moment";

// xlsx (~800 KB) and jspdf (~350 KB) are loaded ON DEMAND inside the two
// download functions below instead of statically. This module is imported by
// DownloadDataButton, which sits on every list page, so static imports here
// dragged both libraries into the app's MAIN bundle even though they are only
// needed when a user actually clicks Download. (Part of the 2026-08-19 bundle
// diet: 5.5 MB main.js.)

// ---------------------------------------------------------------------------
// Value formatters
// ---------------------------------------------------------------------------

const fmtDateTime = (val) =>
  val ? moment(val).format("DD MMM YYYY, h:mm A") : "-";

const fmtDate = (val) => (val ? moment(val).format("DD MMM YYYY") : "-");

// Status mirrors the list page's "Status" column intent: a client is
// Active unless disabled. (Sync state is its own column.)
const fmtStatus = (row) => {
  if (row?.disabled === true || row?.disabled === 1) return "Disabled";
  return "Active";
};

// "Sync Notices" column → last-sync timestamp / processing state. The
// caller passes the right sync field name per portal via the column set.
const fmtSync = (syncField) => (row) => {
  if (row?.is_processing === true || row?.is_processing === 1) {
    return "Syncing…";
  }
  return row?.[syncField] ? fmtDateTime(row[syncField]) : "Not synced";
};

// "Users" column → the many-to-many assignee names (or "Shared").
const fmtUsers = (row) => {
  const list = Array.isArray(row?.assigned_users) ? row.assigned_users : [];
  if (list.length === 0) return "Shared";
  return list
    .map((u) => u.user_name || u.user_email || u.user_id)
    .join(", ");
};

// ---------------------------------------------------------------------------
// Column sets — one per downloadable entity. NO passwords anywhere.
// ---------------------------------------------------------------------------

const CLIENT_COMMON_TAIL = (syncField) => [
  { header: "Status", accessor: fmtStatus },
  { header: "Users", accessor: fmtUsers },
  { header: "Created On", accessor: (r) => fmtDateTime(r.created_at) },
];

const COLUMN_SETS = {
  "clients-income-tax": [
    { header: "Client Name", accessor: "client_name" },
    { header: "PAN", accessor: "username" },
    { header: "Date of Birth", accessor: (r) => fmtDate(r.dob) },
    ...CLIENT_COMMON_TAIL("last_income_tax_sync"),
  ],
  "clients-gst": [
    { header: "Client Name", accessor: "client_name" },
    { header: "GSTIN", accessor: (r) => r.gstin || "-" },
    { header: "Username", accessor: "gst_username" },
    ...CLIENT_COMMON_TAIL("last_gst_sync"),
  ],
  "clients-tds": [
    { header: "Client Name", accessor: "client_name" },
    { header: "Username", accessor: "tds_username" },
    { header: "TAN Number", accessor: (r) => r.tan_number || "-" },
    ...CLIENT_COMMON_TAIL("last_tds_sync"),
  ],

  // Notices — only necessary, downloadable fields. Notice list payloads
  // vary; accessors fall back gracefully when a field is absent.
  // Columns mirror the on-screen GST Notices list
  // (GstNoticesList.jsx columns array). View Summary action column
  // omitted. Date format matches the in-row Tag's YYYY-MM-DD →
  // DD-MM-YYYY transform; "N/A" / "Pending" fallbacks match the
  // in-row Tag defaults.
  "notices-gst": [
    { header: "Client Name", accessor: (r) => r.client_name || "-" },
    { header: "Reference ID", accessor: (r) => r.ref_id || "-" },
    { header: "Description", accessor: (r) => r.type || "-" },
    { header: "Issued Date", accessor: (r) => (r.issue_date ? String(r.issue_date).split("-").reverse().join("-") : "N/A") },
    { header: "Due Date", accessor: (r) => (r.due_date ? String(r.due_date).split("-").reverse().join("-") : "N/A") },
    { header: "Response Status", accessor: (r) => r.response_status || "Pending" },
    { header: "Portal Status", accessor: (r) => r.notice_status || "N/A" },
  ],
  // Columns mirror the on-screen E-Proceedings list (Eproceedings.jsx
  // `eProceedingColumns`). Date format matches the table tag's
  // YYYY-MM-DD → DD-MM-YYYY transform; missing dates render "N/A"; the
  // Response Status default ("Pending") matches the in-row tag fallback.
  "notices-it-ep": [
    { header: "Client Name", accessor: (r) => r.client_name || "-" },
    { header: "Notice Communication ID", accessor: (r) => r.notice_communication_reference_id || "-" },
    { header: "Assessment Year", accessor: (r) => r.assessment_year || "-" },
    { header: "Issued Date", accessor: (r) => (r.notice_sent_date ? String(r.notice_sent_date).split("-").reverse().join("-") : "N/A") },
    { header: "Response Due Date", accessor: (r) => (r.response_due_date ? String(r.response_due_date).split("-").reverse().join("-") : "N/A") },
    { header: "Response Status", accessor: (r) => r.response_status || "Pending" },
    { header: "Portal Status", accessor: (r) => r.proceeding_status || "N/A" },
  ],
  // Columns mirror the on-screen Response to Outstanding Demand list
  // (Eproceedings.jsx `responseToOutstandingColumns`). View Summary
  // action column omitted (no exportable data). Date format matches
  // the in-row Tag's YYYY-MM-DD → DD-MM-YYYY transform; "N/A" / "Pending"
  // fallbacks match the in-row Tag defaults.
  "notices-it-demand": [
    { header: "Client Name", accessor: (r) => r.client_name || "-" },
    { header: "Demand Reference No", accessor: (r) => r.demand_reference_no || "-" },
    { header: "Section Code", accessor: (r) => r.section_code || "-" },
    { header: "Assessment Year", accessor: (r) => r.assessment_year || "-" },
    { header: "Date of Demand Raised", accessor: (r) => (r.date_of_demand_raised ? String(r.date_of_demand_raised).split("-").reverse().join("-") : "N/A") },
    { header: "Response Status", accessor: (r) => r.response_status || "Pending" },
    { header: "Portal Status", accessor: (r) => r.notice_status || "N/A" },
  ],
  // Columns mirror the on-screen TDS Notices list
  // (TDSNoticesList.jsx columns array). No View Summary action column
  // on the TDS list. Date format matches the in-row Tag's YYYY-MM-DD
  // → DD-MM-YYYY transform; "-" fallback for missing dates matches the
  // in-row render fallback (TDS uses "-" rather than "N/A"). Response
  // Status defaults to "Pending"; Portal Status defaults to "N/A".
  "notices-tds": [
    { header: "Client Name", accessor: (r) => r.client_name || "-" },
    { header: "Financial Year", accessor: (r) => r.fy || "-" },
    { header: "Form Type", accessor: (r) => r.form_type || "-" },
    { header: "Issued Date", accessor: (r) => (r.issue_date ? String(r.issue_date).split("-").reverse().join("-") : "-") },
    { header: "Due Date", accessor: (r) => (r.due_date ? String(r.due_date).split("-").reverse().join("-") : "-") },
    { header: "Quarter", accessor: (r) => r.quarter || "-" },
    { header: "Response Status", accessor: (r) => r.response_status || "Pending" },
    { header: "Portal Status", accessor: (r) => r.notice_status || "N/A" },
  ],
};

export const getColumnSet = (entity) => COLUMN_SETS[entity] || [];

// Resolve a cell value from a record using a column's accessor.
const cellValue = (row, col) => {
  const v = typeof col.accessor === "function" ? col.accessor(row) : row[col.accessor];
  if (v === null || v === undefined) return "";
  return typeof v === "string" ? v : String(v);
};

// ---------------------------------------------------------------------------
// Excel
// ---------------------------------------------------------------------------

export const downloadExcel = async (records, columnSet, { fileName, title } = {}) => {
  const XLSX = await import("xlsx");
  const headers = columnSet.map((c) => c.header);
  const aoa = [headers];
  for (const row of records) {
    aoa.push(columnSet.map((c) => cellValue(row, c)));
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths sized to the longest cell (capped) so the sheet is
  // readable without manual resizing.
  ws["!cols"] = columnSet.map((c, i) => {
    let max = c.header.length;
    for (const row of records) {
      const len = cellValue(row, c).length;
      if (len > max) max = len;
    }
    return { wch: Math.min(Math.max(max + 2, 12), 48) };
  });

  // Freeze the header row.
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  const sheetName = (title || "Data").slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([out], { type: "application/octet-stream" }),
    `${fileName || "data"}.xlsx`
  );
};

// ---------------------------------------------------------------------------
// PDF (manual table layout — landscape A4)
// ---------------------------------------------------------------------------

export const downloadPdf = async (records, columnSet, { fileName, title } = {}) => {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF("l", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const contentWidth = pageWidth - margin * 2;

  let y = 16;

  // Title + meta.
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(10, 30, 84); // NoticeAI navy
  pdf.text(title || "Data Export", margin, y);
  y += 7;
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(90, 90, 90);
  pdf.text(`Exported: ${moment().format("DD MMM YYYY, h:mm A")}`, margin, y);
  pdf.text(`Total records: ${records.length}`, pageWidth - margin, y, {
    align: "right",
  });
  y += 6;

  const cols = columnSet;
  const colWidth = contentWidth / cols.length;
  const rowPadding = 2;
  const lineHeight = 4;
  const fontSize = 7.5;

  const drawHeader = () => {
    pdf.setFillColor(10, 30, 84);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(fontSize);
    const headerHeight = 7;
    let x = margin;
    pdf.rect(margin, y, contentWidth, headerHeight, "F");
    cols.forEach((c) => {
      const lines = pdf.splitTextToSize(c.header, colWidth - 2);
      pdf.text(lines, x + 1.5, y + 4.5);
      x += colWidth;
    });
    y += headerHeight;
  };

  drawHeader();

  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(30, 30, 30);

  records.forEach((row, idx) => {
    // Pre-measure the tallest cell to size the row.
    const cellLines = cols.map((c) =>
      pdf.splitTextToSize(cellValue(row, c) || "-", colWidth - 3)
    );
    const maxLines = Math.max(1, ...cellLines.map((l) => l.length));
    const rowHeight = maxLines * lineHeight + rowPadding * 2;

    if (y + rowHeight > pageHeight - margin) {
      pdf.addPage();
      y = 16;
      drawHeader();
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(30, 30, 30);
    }

    // Zebra striping.
    if (idx % 2 === 0) {
      pdf.setFillColor(244, 247, 252);
      pdf.rect(margin, y, contentWidth, rowHeight, "F");
    }

    let x = margin;
    cols.forEach((c, ci) => {
      pdf.text(cellLines[ci], x + 1.5, y + rowPadding + 3);
      x += colWidth;
    });
    y += rowHeight;
  });

  // Light grid: outer border.
  pdf.setDrawColor(210, 215, 225);
  pdf.save(`${fileName || "data"}.pdf`);
};
